import dayjs from 'dayjs';
import {LANGUAGE_REGEX, MEDIA_URL} from '@/ts/utils/consts';
import {type ChallengeDTO, ReccoChallengeStateDTO} from '@/ts/types/dto/recco/challenge';
import type {MultiChoice} from '@/ts/types/dto/interventions-dto';

const QUERY_OR_HASH = /[?#].*$/su;

/** Route identity of a path: language prefix, query and hash removed. */
function routeTarget(path: string): string {
  const pathname = (path?.replace(QUERY_OR_HASH, '') ?? '') || '/';
  if (!LANGUAGE_REGEX.STARTS_WITH_LANG.test(pathname)) {
    return pathname;
  }
  return (pathname?.replace(LANGUAGE_REGEX.REMOVE_LANG_PREFIX, '') ?? '') || '/';
}

/**
 * Single source of truth for whether a multiple-choice selection satisfies its
 * question constraints: the number of selected options must fall within
 * [minOptions, maxOptions], and if any selected option requires a custom answer
 * (CHECKBOX_WITH_INPUT), that custom answer must be non-empty.
 *
 * Shared by the multiple-choice question leaf components (`question-grid-select`,
 * `question-grid-select-with-input`, `question-image`) as the validity that
 * drives their form-required warning, instead of each re-deriving the rule.
 */
export function isMultiChoiceSelectionValid(
  selectedIds: readonly string[] | null | undefined,
  multiChoice: MultiChoice,
  customAnswer?: string | null,
): boolean {
  const selectedCount = selectedIds?.length ?? 0;
  const withinRange = selectedCount >= multiChoice.minOptions
    && selectedCount <= multiChoice.maxOptions;

  const requiresCustomAnswer = (selectedIds ?? []).some(
    id => multiChoice.options.find(option => option.id === id)?.needsCustomAnswer,
  );
  const hasCustomAnswer = (customAnswer?.trim().length ?? 0) > 0;

  return withinRange && (!requiresCustomAnswer || hasCustomAnswer);
}

/**
 * Discriminated result of {@link multiChoiceValidationMessage}: each i18n key is
 * paired with exactly the interpolation params that key expects.
 */
export type MultiChoiceValidationMessage =
  | {key: 'answer_questionnaire'; named: Record<string, never>}
  | {key: 'select_n_answers'; named: {count: number}}
  | {key: 'select_range_answers'; named: {min: number; max: number}};

/**
 * Picks the i18n message shown by the multiple-choice form-required warning,
 * derived from the question's min/max so it stays meaningful even when the
 * authored question text omits the "choose N" hint:
 *  - at most one required (minOptions <= 1): the generic "answer the questionnaire",
 *    since selecting one option already satisfies the question;
 *  - fixed count (minOptions === maxOptions > 1): "select {count} answers";
 *  - range (minOptions > 1, minOptions !== maxOptions): "select between {min} and {max} answers".
 */
export function multiChoiceValidationMessage(multiChoice: MultiChoice): MultiChoiceValidationMessage {
  const {minOptions, maxOptions} = multiChoice;
  if (minOptions <= 1) {
    return {key: 'answer_questionnaire', named: {}};
  }
  if (minOptions === maxOptions) {
    return {key: 'select_n_answers', named: {count: minOptions}};
  }
  return {key: 'select_range_answers', named: {min: minOptions, max: maxOptions}};
}

/** Checks whether a string is a valid URL. Null/undefined input is treated as invalid. */
export function isValidUrl(value: string | null | undefined): boolean {
  const trimmed = value?.trim();
  if (!trimmed) {
    return false;
  }
  try {
    void new URL(trimmed);
    return true;
  } catch {
    return false;
  }
}

/** Checks whether a string matches the UUID format. Null/undefined input is treated as invalid. */
export function isValidUuid(uuid: string | null | undefined): boolean {
  const trimmed = uuid?.trim();
  if (!trimmed) {
    return false;
  }
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
  return uuidRegex.test(trimmed);
}

/** Removes duplicate values from an array */
export function uniqueOnly<T>(array: T[]): T[] {
  return [...new Set(array)];
}

/** Removes duplicate items from an array using a key extracted by the getter function */
export function uniqueOnlyById<T>(array: T[], getter: (value: T) => number | string): T[] {
  return array.filter((item, index) => array.findIndex((inner) => getter(inner) === getter(item)) === index);
}

/** Returns a promise that resolves after the specified number of milliseconds */
export async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms)); // eslint-disable-line no-promise-executor-return
}

/**
 * Generates a random ID safe for non-secure contexts (HTTP, iOS Safari WebViews).
 * Prefer this over crypto.randomUUID() which requires a secure context.
 */
export function getRandomId(): string {
  return Math.floor(Math.random() * Date.now()).toString(36);
}

/**
 * Accepts either:
 *  - a full URL → returns it unchanged
 *  - a UUID → builds a Directus asset URL on MEDIA_URL
 * Anything else (legacy ids) → also build on MEDIA_URL
 */
export function calculateMediaUrl(input: string): string {
  const value = (input || '').trim();
  if (!value) {
    return '';
  }

  if (isValidUrl(value)) {
    return value;
  }

  if (isValidUuid(value)) {
    return `${MEDIA_URL}/${value}`;
  }

  // Default to treating as media ID if no other conditions match
  return `${MEDIA_URL}/${value}`;
}

/** Directus asset transformation parameters (https://docs.directus.io/reference/files.html#custom-transformations) */
export interface ImageTransformOptions {
  width?: number;
  height?: number;
  left?: number;
  top?: number;
  format?: 'auto' | 'jpg' | 'png' | 'tiff' | 'webp';
}

/**
 * Builds a Directus media URL with transform params merged via the URL API,
 * preserving any query params already present in the src (explicit options win).
 * Accepts a UUID or a full URL (resolved through calculateMediaUrl).
 */
export function buildImageTransformUrl(src: string, options: ImageTransformOptions): string {
  const base = calculateMediaUrl(src);
  if (!base) {
    return base;
  }
  const url = new URL(base);
  if (options.width) {
    url.searchParams.set('width', `${Math.ceil(options.width)}`);
  }
  if (options.height) {
    url.searchParams.set('height', `${Math.ceil(options.height)}`);
  }
  if (options.left !== undefined) { // eslint-disable-line no-undefined
    url.searchParams.set('left', `${options.left}`);
  }
  if (options.top !== undefined) { // eslint-disable-line no-undefined
    url.searchParams.set('top', `${options.top}`);
  }
  if (options.format) {
    url.searchParams.set('format', options.format);
  }
  return url.toString();
}

/**
 * Quantizes the device pixel ratio into a small set of size factors (1 / 1.5 / 2)
 * so hi-DPI screens get sharp images while Directus variant cardinality stays bounded.
 * Capped at 2: source assets top out at 1920px, so 3x requests would only upscale.
 */
export function calculateImageSizeFactor(devicePixelRatio: number): number {
  if (devicePixelRatio >= 2) {
    return 2;
  }
  if (devicePixelRatio > 1) {
    return 1.5;
  }
  return 1;
}

/** Groups array items into a record keyed by the result of the key extractor */
export function groupBy<T>(xs: T[], keyExtractor: (item: T) => string): Record<string, T[]> {
  return xs.reduce<Record<string, T[]>>((resVal, curVal) => {
    const key = keyExtractor(curVal);
    if (!resVal[key]) {
      resVal[key] = [];
    }
    resVal[key].push(curVal);
    return resVal;
  }, {});
}


/** Converts a hex color string to an rgba() CSS value with the given alpha. Returns 'transparent' for null/undefined input. */
export function hexToRGBA(hex: string | null | undefined, alpha: number): string {
  if (!hex) {
    return 'transparent';
  }
  const red = parseInt(hex?.slice(1, 3) ?? '', 16);
  const green = parseInt(hex?.slice(3, 5) ?? '', 16);
  const blue = parseInt(hex?.slice(5, 7) ?? '', 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/** Formats an ISO datetime string as YYYYMMDDTHHMMSSZ for ICS calendar files. Returns empty string for null/undefined input. */
export function formatICSDate(utcDateStr: string | null | undefined): string {
  const datePart = (utcDateStr?.replace(/[-:]/gu, '') ?? '').split('.')[0] ?? '';
  if (!datePart) {
    return '';
  }
  return `${datePart}Z`;
}

/** Formats a Date as a YYYY-MM-DD string */
export function formatDateYMD(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}

/**
 * How many days the given date sits after the start of its week: 0 on the first day, 6 on the last.
 * dayjs .day(): 0=Sun, 1=Mon, ..., 6=Sat; `weekStartsOn` is numbered the same way, Monday by
 * default and Sunday where the reader's country starts its week then (`useDateFormat`).
 */
export function getDayOfWeekIndex(date: dayjs.Dayjs, weekStartsOn: 0 | 1 = 1): number {
  return (date.day() - weekStartsOn + 7) % 7;
}

/** Returns the first day of the week containing the given date, Monday unless told otherwise. */
export function getStartOfWeek(date: dayjs.Dayjs, weekStartsOn: 0 | 1 = 1): dayjs.Dayjs {
  return date.subtract(getDayOfWeekIndex(date, weekStartsOn), 'day');
}

/**
 * Returns the first Monday on or after the given date.
 */
export function getNextOrSameMonday(date: dayjs.Dayjs): dayjs.Dayjs {
  return date.add((8 - date.day()) % 7, 'day');
}

/** Generates a UUID with all alphanumeric characters replaced by zeros */
export function getNulledUuid(): string {
  return crypto.randomUUID().replace(/[a-zA-Z0-9]/gu, '0');
}

/** Clamps a BE 0–1 completion fraction to a 0–100 integer percent. */
export function progressFractionToPct(fraction: number): number {
  return Math.max(0, Math.min(100, Math.round(fraction * 100)));
}

/**
 * True when two paths address the same route. The language prefix, query and hash are
 * ignored, so `/en/explore?from=x` and the prefix-less `/explore` count as the same
 * target (route links are authored with and without the prefix across the app).
 */
export function isSameRoutePath(a: string, b: string): boolean {
  return routeTarget(a) === routeTarget(b);
}

/**
 * True when two pieces of fetched data hold the same values, whatever objects carry them. For
 * plain JSON from the API: keys are compared in order, and an undefined field counts as absent.
 */
export function hasSameContent(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * The whole minutes a card prints for a piece of media, rounded up so a 90-second clip reads
 * "2 min" rather than claiming one — the rounding the mobile apps already use, so the same piece
 * reads the same on every client. Null for a missing, zero or non-finite length, which is a length
 * the CMS does not hold rather than a zero-minute one: the caller then shows the kind alone.
 */
export function mediaDurationMinutes(seconds: number | null | undefined): number | null {
  if (!Number.isFinite(seconds) || Number(seconds) <= 0) {
    return null;
  }
  return Math.ceil(Number(seconds) / 60);
}

/** Formats a duration in seconds as a "m:ss" string. Returns "0:00" for null/undefined or non-finite input. */
export function formatDuration(seconds: number | null | undefined): string {
  const safeSeconds = Number.isFinite(seconds) ? Number(seconds) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = String(safeSeconds % 60).padStart(2, '0');
  return `${minutes}:${remainingSeconds}`;
}

/**
 * Formats a numeric value as a 5-character US ZIP code with leading zeros.
 * Returns the rounded raw value as-is when it falls outside [0, 99999] so
 * corrupt or unexpected data remains visible instead of being hidden.
 */
export function formatZipCode(value: number | null | undefined): string {
  if (value === null || value === undefined) { // eslint-disable-line no-undefined
    return '';
  }
  const zipLength = 5;
  const zipMaxValue = 99999;
  const rounded = Math.round(value);
  if (!Number.isFinite(rounded)) {
    return String(rounded);
  }
  if (rounded < 0 || rounded > zipMaxValue) {
    return String(rounded);
  }
  return String(rounded).padStart(zipLength, '0');
}

/** Rounds a number to the specified number of decimal places */
export function roundTo(value: number, decimalPlaces: number): number {
  const multiplier = 10 ** decimalPlaces;
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
}

/** Computes the SHA-256 hash of a string and returns it as a hex string */
export async function hashString(str: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(byte => Number(byte).toString(16).padStart(2, '0')).join('');
}

/** Calculates the Levenshtein edit distance between two strings. Treats null/undefined as empty strings. */
export function detectLevenshteinDistance(a: string | null | undefined, b: string | null | undefined): number {
  const aLen = a?.length ?? 0;
  const bLen = b?.length ?? 0;
  if (aLen === 0 && bLen === 0) {
    return 0;
  }
  if (a === b) {
    return 0;
  }

  const matrix = Array.from({length: bLen + 1}, (_, i) =>
    // eslint-disable-next-line @typescript-eslint/no-shadow, no-nested-ternary
    Array.from({length: aLen + 1}, (_, j) => (i === 0 ? j : j === 0 ? i : 0)));

  for (let i = 1; i <= bLen; i++) {
    for (let j = 1; j <= aLen; j++) {
      if (b?.[i - 1] === a?.[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1,      // deletion
        );
      }
    }
  }

  return matrix[bLen][aLen];
}

/**
 * Returns the latest (most recent) timestamp from an array of date strings.
 * @param dates - Array of date strings (e.g., '2024-01-04T22:38:02.821935Z' or '2024-01-04')
 * @returns The latest timestamp in milliseconds, or 0 if the array is empty
 */
export function getLatestTimestamp(dates: string[]): number {
  if (!dates.length) {return 0;}
  return Math.max(...dates.map(d => new Date(d).getTime()));
}

/**
 * Returns the earliest (oldest) timestamp from an array of date strings.
 * @param dates - Array of date strings (e.g., '2024-01-04T22:38:02.821935Z' or '2024-01-04')
 * @returns The earliest timestamp in milliseconds, or 0 if the array is empty
 */
export function getEarliestTimestamp(dates: string[]): number {
  if (!dates.length) {return 0;}
  return Math.min(...dates.map(d => new Date(d).getTime()));
}

/**
 * Detects whether a challenge has just auto-expired and is awaiting a one-time acknowledgment.
 * The BE surfaces an expired run once as state=FAILED with pendingStateAcknowledgment=true, then
 * clears the flag — so this signals the moment to show the "challenge expired" notice to the user.
 */
export function isChallengeExpiredPendingAck(challenge: ChallengeDTO | null | undefined): boolean {
  return challenge?.state === ReccoChallengeStateDTO.FAILED && Boolean(challenge.pendingStateAcknowledgment);
}

/**
 * Whether a questionnaire opened now must render read-only (IPD-2772: completed
 * questionnaires must not be modified). Evaluate this once at open time and store the
 * result — isAnswered flips true mid-flow when the completing PUT replaces the store,
 * and a reactive binding would strip the Complete button off the still-open modal.
 */
export function isQuestionnaireReadonlyOnOpen(questionnaire: {isAnswered: boolean}): boolean {
  return questionnaire.isAnswered;
}
